import { date } from 'quasar'
import { SESSION_ADMISSION_TYPES } from 'src/models/activity/Activity'
import ActivitySession from 'src/models/activity-session/ActivitySession'
import Model, { encoders, ID, IsoDateTime, MODEL_LEVEL } from 'src/models/Model'
import TicketType from 'src/models/showing/TicketType'
import User from 'src/models/user/User'

const { addToDate, formatDate } = date

export default class ActivityTicket extends Model {
  static name = 'ActivityTicket'

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Customer]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      state: {
        type: String,
        output: true,
        default: 'draft',
        sortable: true
      },
      activitySession: {
        type: ActivitySession,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      ticketType: {
        type: TicketType,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      activityUnitQuantity: {
        type: Number,
        default: 1
      },
      user: {
        type: User,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      price: {
        type: Number,
        output: true,
        default: null,
        computed () {
          if (this.state !== 'draft') return this.data.price

          return this.finalTicketType.price
        }
      },
      entryTime: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      exitTime: {
        type: IsoDateTime,
        default: null,
        sortable: true
      }
    }

    this.computed = {
      siteEntryTime () {
        if (!this.entryTime) return this.entryTime

        return this.entryTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      siteExitTime () {
        if (!this.exitTime) return this.exitTime

        return this.exitTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      activity () {
        return this.activitySession.activity
      },
      finalTicketType () {
        const priceCardItem = this.activitySession && _.find(
          this.activitySession.priceCard.priceCardItems.data,
          priceCardItem => priceCardItem.ticketType?.id === this.ticketType.id
        )

        if (priceCardItem && priceCardItem.hasCustomPrice) {
          // CLONE THE TICKET TYPE AND OVERRIDE THE PRICE
          const clone = priceCardItem.ticketType.clone()
          clone.price = priceCardItem.customPrice
          clone.factor = this.activityUnitQuantity

          const prices = {
            price: clone.factorPrice
          }
          clone.destroy()
          return prices
        }

        this.ticketType.factor = this.activityUnitQuantity
        return { price: this.ticketType.factorPrice }
      },
      activityUnitQuantityDescription () {
        if (this.activity.sessionAdmissionType === SESSION_ADMISSION_TYPES.SESSION || this.activity.maxConsecutiveActivityUnits === 1) {
          return null
        } else {
          return `${this.activityUnitQuantity} ${this.activityUnitQuantity > 1 ? this.activity.activityUnitDescriptionPlural : this.activity.activityUnitDescription}`
        }
      }
    }

    this.paginate = true
  }

  formattedTime (showDate = false, relative = false) {
    const today = formatDate(new Date(), 'YYYY-MM-DD')
    const tomorrow = formatDate(addToDate(new Date(), { days: 1 }), 'YYYY-MM-DD')

    const sessionDate = formatDate(this.siteEntryTime, 'YYYY-MM-DD')

    const entryTime = formatDate(this.siteEntryTime, 'h:mm a')
    const exitTime = formatDate(this.siteExitTime, 'h:mm a')

    if (!showDate) {
      return `${entryTime} until ${exitTime}`
    }

    let date = ''

    if (relative && sessionDate === today) {
      date = 'Today'
    } else if (relative && sessionDate === tomorrow) {
      date = 'Tomorrow'
    } else {
      date = formatDate(this.siteEntryTime, 'MMM Do')
    }

    return `${date} from ${entryTime} until ${exitTime}`
  }
}
